import React, { useId, useState } from 'react';

// MUI Components
import { TextField, Autocomplete, Divider, Typography, Box, Checkbox, Chip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

// Icons
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import { CrossIconCIcon, SelectCloseCIcon } from '~/assets/images';

// styles
import './styles.scss';

const OlcDropdown = (props) => {
  const {
    placeholder,
    options,
    selectedValue,
    setSelectedValue,
    search,
    updateErrors,
    name,
    loading,
    disable = false,
    multiple = false,
    disableClearable = false,
    templateBuilder = false,
    filterAutoComplete = false,
    error = false,
    tagsClass = false,
    campaign = false,
    className = '',
    newDesign = false,
    readOnlyInput = false,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownId = useId();

  const updateValue = (event, newValue) => {
    event.preventDefault();
    !newValue?.func ? setSelectedValue(newValue) : undefined;
    updateErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
    newValue?.func ? newValue?.func() : undefined;
    !readOnlyInput && search('');
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = (
    <CheckBoxIcon
      fontSize="small"
      sx={{
        color: '#ED5C2F',
      }}
    />
  );

  const clearIcon = tagsClass ? null : selectedValue &&
    (Array.isArray(selectedValue) ? selectedValue.length > 0 : true) ? (
    <img
      src={newDesign ? SelectCloseCIcon : CrossIconCIcon}
      // style={{ opacity: '0.6' }}
      style={{
        top: '5px',
      }}
      alt="clear"
      onClick={() => setSelectedValue(null)}
    />
  ) : null;

  const renderOption = (props, option, { selected }) => {
    return (
      <div key={option.id}>
        {loading && options.indexOf(option) === 0 ? (
          <Box>
            <LinearProgress
              sx={{
                backgroundColor: 'white',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ED5C2F',
                },
              }}
            />
          </Box>
        ) : null}
        <li {...props} key={option.id}>
          {option.custom ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {option.icon && <img src={option.icon} alt="icon" />}
              <Typography sx={{ color: option.custom ? '#ED5C2F' : '' }}>{option.label}</Typography>
            </Box>
          ) : multiple ? (
            <>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected || selectedValue?.includes(option.label)}
              />
              {option.label}
            </>
          ) : (
            <Typography className={templateBuilder && 'tempBuilderOptions'}>
              {option.label}
            </Typography>
          )}
        </li>
        {className !== 'newAutocompleteDesign' ||
          (className !== 'olcNewDropdown' && <Divider className="listDivider" />)}
      </div>
    );
  };

  const getAutocompleteClassName = (name) => {
    const componentNames = ['template', 'returnAddress', 'date'];
    return componentNames.includes(name)
      ? 'autocompletePaper'
      : newDesign
        ? 'newAutocompleteMenuDesign olcDropDowns'
        : '';
  };

  return (
    <Autocomplete
      className={
        multiple
          ? `olcDropdown multipleAutoSelect ${tagsClass && 'tagsUpdateSelect'}`
          : templateBuilder
            ? 'olcDropdown tempAutocomplete'
            : `olcDropdown genericAutocomplete ${filterAutoComplete ? 'filterAutoComplete' : ''} ${error ? 'errorAutocomplete' : ''} ${className}`
      }
      multiple={multiple}
      renderTags={(value, getTagProps) =>
        tagsClass
          ? null
          : value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.label}
                label={option.label}
                deleteIcon={<HighlightOffIcon sx={{ color: '#ED5C2F' }} />}
              />
            ))
      }
      clearIcon={null}
      sx={{
        maxWidth: campaign ? '100%' : '564px',
        background: campaign ? 'white' : 'transparent',
        '& .MuiAutocomplete-clearIndicator': {
          visibility: selectedValue ? 'visible' : 'hidden',
        },
      }}
      slotProps={{
        popper: {
          className: 'olcDropdownPopper',
        },
        paper: {
          sx: {
            boxShadow: '2px 7px 13px 0 rgba(0, 0, 0, 0.16) !important',
          },
        },
        popupIndicator: {
          sx: {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          disableRipple: true,
        },
      }}
      limitTags={multiple && tagsClass ? 3 : multiple ? 1 : undefined}
      disabled={disable}
      disableCloseOnSelect={multiple}
      disableClearable
      id={dropdownId}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => {
        const optLabel = typeof option === 'string' ? option : option.label;
        const valLabel = typeof value === 'string' ? value : value.label;
        return optLabel === valLabel;
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      value={selectedValue}
      loading={loading}
      onChange={(event, newValue) => {
        updateValue(event, newValue);
        !multiple && !readOnlyInput
          ? search(newValue?.label ? newValue?.label : newValue)
          : undefined;
      }}
      onKeyUp={(event) => !readOnlyInput && search(event.target.value)}
      onBlur={(event) => !readOnlyInput && search(event.target.value || '')}
      onFocus={(event) => !readOnlyInput && search(event.target.value || '')}
      options={options}
      onOpen={() => {
        setMenuOpen(true);
        // Clear any text selection when dropdown opens
        if (readOnlyInput && selectedValue) {
          requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            if (activeElement && activeElement.tagName === 'INPUT') {
              activeElement.setSelectionRange(0, 0);
            }
          });
        }
      }}
      onClose={() => setMenuOpen(false)}
      popupIcon={
        <OlcDropdownChevron disabled={disable} open={menuOpen} style={{ marginRight: '-4px' }} />
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selectedValue?.length > 0 && tagsClass
              ? placeholder
              : selectedValue?.length > 0
                ? ''
                : placeholder
          }
          size="small"
          className={templateBuilder ? 'tempAutoField' : ''}
          inputProps={{
            ...params.inputProps,
            readOnly: readOnlyInput,
            style: readOnlyInput ? { cursor: 'pointer' } : undefined,
            onFocus: (e) => {
              if (readOnlyInput && selectedValue) {
                // Clear any text selection on focus - use requestAnimationFrame to not interfere with dropdown
                requestAnimationFrame(() => {
                  e.target.setSelectionRange(0, 0);
                });
              }
            },
            onSelect: (e) => {
              if (readOnlyInput && selectedValue) {
                // Clear selection if it happens
                requestAnimationFrame(() => {
                  e.target.setSelectionRange(0, 0);
                });
              }
            },
          }}
          sx={
            readOnlyInput
              ? {
                  '& input': {
                    cursor: 'pointer !important',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    caretColor: 'transparent',
                    '&::selection': {
                      background: 'transparent',
                    },
                    '&::-moz-selection': {
                      background: 'transparent',
                    },
                  },
                }
              : undefined
          }
        />
      )}
      renderOption={renderOption}
      classes={{
        paper: [getAutocompleteClassName(name), tagsClass ? 'tagsPaper' : '']
          .filter(Boolean)
          .join(' '),
      }}
      getOptionDisabled={(option) => option.customPlaceholder}
      ListboxProps={{
        className: 'olcSingleSelectListbox',
        style: { maxHeight: 250 },
      }}
    />
  );
};

export default OlcDropdown;
